import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, FileText } from 'lucide-react';

interface SulfurBurnerTwoCTAProps {
  layout?: 'horizontal' | 'vertical' | 'stacked';
  size?: 'default' | 'large';
  showPositioning?: boolean;
  className?: string;
}

const SulfurBurnerTwoCTA: React.FC<SulfurBurnerTwoCTAProps> = ({
  layout = 'horizontal',
  size = 'default',
  showPositioning = true,
  className = ''
}) => {
  const buttonSizeClasses = size === 'large'
    ? 'px-8 py-4 text-lg'
    : 'px-6 py-3 text-base';

  const iconSize = size === 'large' ? 24 : 20;

  if (layout === 'stacked') {
    return (
      <div className={`space-y-6 ${className}`}>
        {showPositioning && (
          <p className="text-lg text-gray-700 font-medium text-center max-w-3xl mx-auto">
            Start with pH + peak GPM. If you have a water/soil report, we'll tighten targets and reduce surprises.
          </p>
        )}

        <div className="space-y-4 max-w-2xl mx-auto">
          <Link
            to="/instant-quote"
            className={`w-full bg-ctc-blue text-white ${buttonSizeClasses} rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 font-semibold shadow-lg hover:shadow-xl`}
          >
            <Calculator size={iconSize} />
            Instant Size + Quote
          </Link>
          <p className="text-sm text-gray-600 text-center -mt-2">
            Takes 60 seconds. No lab report needed.
          </p>

          <Link
            to="/chemistry-review"
            className={`w-full bg-white text-ctc-blue border-2 border-ctc-blue ${buttonSizeClasses} rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-3 font-semibold`}
          >
            <FileText size={iconSize} />
            Free Chemistry Review + Action Plan
          </Link>
          <p className="text-sm text-gray-600 text-center -mt-2">
            Uploads optional. We'll start with pH + GPM and tell you what to test next.
          </p>
        </div>
      </div>
    );
  }

  if (layout === 'vertical') {
    return (
      <div className={`space-y-6 ${className}`}>
        {showPositioning && (
          <p className="text-base text-gray-700 font-medium">
            Start with pH + peak GPM. If you have a water/soil report, we'll tighten targets and reduce surprises.
          </p>
        )}

        <div className="space-y-4">
          <div>
            <Link
              to="/instant-quote"
              className={`w-full bg-ctc-blue text-white ${buttonSizeClasses} rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 font-semibold shadow-md hover:shadow-lg`}
            >
              <Calculator size={iconSize} />
              Instant Size + Quote
            </Link>
            <p className="text-sm text-gray-600 mt-2">
              Takes 60 seconds. No lab report needed.
            </p>
          </div>

          <div>
            <Link
              to="/chemistry-review"
              className={`w-full bg-white text-ctc-blue border-2 border-ctc-blue ${buttonSizeClasses} rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-3 font-semibold`}
            >
              <FileText size={iconSize} />
              Free Chemistry Review + Action Plan
            </Link>
            <p className="text-sm text-gray-600 mt-2">
              Uploads optional. We'll start with pH + GPM and tell you what to test next.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {showPositioning && (
        <p className="text-lg text-gray-700 font-medium text-center max-w-3xl mx-auto">
          Start with pH + peak GPM. If you have a water/soil report, we'll tighten targets and reduce surprises.
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch">
        <div className="flex-1 max-w-md">
          <Link
            to="/instant-quote"
            className={`w-full bg-ctc-blue text-white ${buttonSizeClasses} rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 font-semibold shadow-lg hover:shadow-xl`}
          >
            <Calculator size={iconSize} />
            Instant Size + Quote
          </Link>
          <p className="text-sm text-gray-600 mt-2 text-center">
            Takes 60 seconds. No lab report needed.
          </p>
        </div>

        <div className="flex-1 max-w-md">
          <Link
            to="/chemistry-review"
            className={`w-full bg-white text-ctc-blue border-2 border-ctc-blue ${buttonSizeClasses} rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-3 font-semibold`}
          >
            <FileText size={iconSize} />
            Free Chemistry Review + Action Plan
          </Link>
          <p className="text-sm text-gray-600 mt-2 text-center">
            Uploads optional. We'll start with pH + GPM and tell you what to test next.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SulfurBurnerTwoCTA;
